import Container, { Service } from 'typedi';
import {
    IAuthenticationResult,
    IAuthenticationService,
    IAuthenticationStorage,
    IAutologinAuthenticationService,
    IAutoLoginResponseModel,
    ILoginOptions,
    IRegistrationAutoLoginModel,
} from '../models';

import { AuthenticationFlowType } from '../enums';
import { loadAuthenticationService } from './authentication.service.loader';
import { StorageItemEnum } from '../../../models/enums/storage-enums';
import { ActionID, ISessionChannel, MessageBroker } from '@sparkware/uc-sdk-core';
import { RegistrationAutoLoginDataBuilder } from './registration-autologindata.builder.service';
import { loadAutoLoginAuthenticationService } from './autologin-authentication.service.loader';

import {
    LocalSimpleStoreService,
    SessionSimpleStoreService,
} from '../../storage/implementations/simple-store';
import { loadAuthenticationStorage } from '../utils/authentication.storage.loader';
import merge from 'lodash/merge';

@Service()
export class RegistrationAuthenticationService {
    private _autoLoginAuthenticationService?: IAutologinAuthenticationService;
    private _authenticationService?: IAuthenticationService;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;
    private readonly _registrationAutoLoginDataBuilder: RegistrationAutoLoginDataBuilder;
    private readonly _sessionChannel: ISessionChannel;
    private _authenticationStorage?: IAuthenticationStorage;

    constructor() {
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._registrationAutoLoginDataBuilder = Container.get(RegistrationAutoLoginDataBuilder);
        this._sessionChannel = MessageBroker.getInstance().session;
    }

    public autoLogin = async (
        data: any,
        options?: ILoginOptions,
    ): Promise<IAuthenticationResult<IAutoLoginResponseModel>> => {
        this._authenticationService = await loadAuthenticationService();
        this._autoLoginAuthenticationService = await loadAutoLoginAuthenticationService();
        this._authenticationStorage = await loadAuthenticationStorage();
        this._authenticationStorage.setLoginOptions(this._enhanceLoginOptions(options));

        return this._authenticationService.executeWithAnalytics(
            AuthenticationFlowType.Registration,
            async (): Promise<IAuthenticationResult<IAutoLoginResponseModel>> => {
                const autoLoginData: IRegistrationAutoLoginModel = await this._createAutoLoginData(
                    data,
                );

                const result = await this._autoLoginAuthenticationService.autoLogin(autoLoginData);
                const { response, errorResponse } = result || {};
                if (errorResponse || !response?.Response?.BearerToken) {
                    this._sessionSimpleStoreService.remove(
                        StorageItemEnum.CashierOpenStartTimestamp,
                    );
                    if (
                        this._localSimpleStoreService.get(StorageItemEnum.ActionStarted) ===
                        ActionID.register
                    ) {
                        this._sessionChannel.topics.actionCompleted.publish(
                            { publisher: 'AuthenticationService' },
                            {},
                        );
                    }
                }

                return result;
            },
        );
    };

    private _createAutoLoginData = async (data): Promise<IRegistrationAutoLoginModel> => {
        if (!data) return null;

        return await this._registrationAutoLoginDataBuilder.Build(data);
    };

    private _enhanceLoginOptions = (options: ILoginOptions = {}): ILoginOptions => {
        const defaultRegistrationOptions: ILoginOptions = {
            postLogin: {
                ActivityHistory: { disabled: true },
                StartFavouriteSportsFlow: { isRegistration: true },
                ShowPlayerWinLoss: { isRegistration: true },
            },
        };

        return merge(defaultRegistrationOptions, options);
    };
}
